import { useGetAgentDashboard } from "@workspace/api-client-react";
import {
  Activity,
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  ExternalLink,
  Layers,
  Mail,
  Phone,
  Plus,
  UserCircle,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { formatZAR } from "@/lib/formatters";

// Openserve partner portal where field-sales agents capture new customer leads.
const OPENSERVE_PORTAL_URL = "https://partners.openserve.co.za";

type StatTone = "navy" | "green" | "amber" | "red";

const STAT_TONES: Record<StatTone, { bg: string; icon: string }> = {
  navy: { bg: "bg-[#0B132B]/10", icon: "text-[#0B132B]" },
  green: { bg: "bg-[#00A859]/10", icon: "text-[#00A859]" },
  amber: { bg: "bg-amber-500/10", icon: "text-amber-600" },
  red: { bg: "bg-red-500/10", icon: "text-red-600" },
};

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: LucideIcon;
  tone: StatTone;
}) {
  const t = STAT_TONES[tone];
  return (
    <Card className="border-none shadow-sm bg-white overflow-hidden rounded-xl group hover:shadow-md transition-all">
      <CardContent className="p-4 relative">
        <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity">
          <Icon className={`w-12 h-12 ${t.icon}`} />
        </div>
        <div
          className={`w-8 h-8 rounded-full ${t.bg} flex items-center justify-center mb-3`}
        >
          <Icon className={`w-4 h-4 ${t.icon}`} />
        </div>
        <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
        <p className="text-2xl font-bold text-[#0B132B]">{value}</p>
      </CardContent>
    </Card>
  );
}

export function AgentDashboard() {
  const { data: dashboard, isLoading, error } = useGetAgentDashboard();

  if (isLoading) {
    return (
      <div className="px-5 pt-6 pb-4 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/2 rounded mb-6"></div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="px-5 pt-6 text-red-500">Failed to load dashboard.</div>
    );
  }

  const { leadStats } = dashboard;

  return (
    <>
      <section className="px-5 pt-6 pb-4">
        <h1 className="text-2xl font-bold text-[#0B132B] mb-1">Hello</h1>
        <p className="text-sm text-gray-500 mb-6">
          Here is your sales performance today.
        </p>

        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-[#0B132B]">Leads overview</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-6">
          <StatCard
            label="Total Leads"
            value={leadStats.total}
            icon={Layers}
            tone="navy"
          />
          <StatCard
            label="Converted"
            value={leadStats.converted}
            icon={CheckCircle2}
            tone="green"
          />
          <StatCard
            label="In Progress"
            value={leadStats.inProgress}
            icon={Clock}
            tone="amber"
          />
          <StatCard
            label="Failed"
            value={leadStats.failed}
            icon={XCircle}
            tone="red"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <StatCard
            label="Active Clients"
            value={dashboard.activeClients}
            icon={Activity}
            tone="green"
          />

          <Card className="border-none shadow-sm bg-[#0B132B] text-white overflow-hidden rounded-xl relative group hover:shadow-md transition-all">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10 blur-2xl"></div>
            <CardContent className="p-4 relative">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
              </div>
              <p className="text-xs text-gray-300 font-medium mb-1">
                Balance Due
              </p>
              <p className="text-2xl font-bold tracking-tight">
                {formatZAR(dashboard.balanceDueCents)}
              </p>
            </CardContent>
          </Card>
        </div>

        <Button
          onClick={() =>
            window.open(OPENSERVE_PORTAL_URL, "_blank", "noopener,noreferrer")
          }
          className="w-full bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl py-6 text-base font-semibold shadow-lg shadow-[#E6007E]/20 transition-all hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add New Lead (Openserve Portal)
          <ExternalLink className="w-4 h-4 opacity-80" />
        </Button>
      </section>

      {dashboard.accountManager ? (
        <section className="px-5 mt-4">
          <Card className="border-gray-100 shadow-sm rounded-xl overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <UserCircle className="w-4 h-4 text-[#0B132B]" />
                <h2 className="text-sm font-bold text-[#0B132B]">
                  Your Account Manager
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0B132B] flex items-center justify-center shrink-0">
                  <span className="text-white text-sm font-bold">
                    {(dashboard.accountManager.name ?? "")
                      .split(" ")
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#0B132B] text-sm">
                    {dashboard.accountManager.name}
                  </p>
                  <div className="flex flex-col gap-1 mt-1">
                    <a
                      href={`mailto:${dashboard.accountManager.email}`}
                      className="flex items-center gap-1.5 text-xs text-[#E6007E] hover:underline truncate"
                    >
                      <Mail className="w-3 h-3 shrink-0" />
                      {dashboard.accountManager.email}
                    </a>
                    {dashboard.accountManager.phone ? (
                      <a
                        href={`tel:${dashboard.accountManager.phone}`}
                        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#0B132B]"
                      >
                        <Phone className="w-3 h-3 shrink-0" />
                        {dashboard.accountManager.phone}
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      ) : null}

      <section className="px-5 mt-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#0B132B]">Recent Leads</h2>
          <Button
            variant="ghost"
            className="text-sm text-[#E6007E] hover:bg-[#E6007E]/10 hover:text-[#E6007E] h-8 px-2"
          >
            View All
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          {dashboard.recentOrders.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No recent leads found.
            </p>
          ) : (
            dashboard.recentOrders.map((order) => (
              <Card
                key={order.id}
                className="border-gray-100 shadow-sm rounded-xl overflow-hidden hover:border-[#E6007E]/30 transition-colors cursor-pointer group"
              >
                <CardContent className="p-0">
                  <div className="p-4 flex flex-col gap-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-[15px] text-[#0B132B]">
                          {order.customerName}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {order.address}
                        </p>
                      </div>
                      <StatusBadge status={order.status} />
                    </div>

                    <div className="h-px w-full bg-gray-50"></div>

                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-0.5">
                          {order.package}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {order.submittedAt
                            ? new Date(order.submittedAt).toLocaleDateString()
                            : "N/A"}{" "}
                          • {order.reference}
                        </p>
                      </div>
                      <p className="font-bold text-sm text-[#0B132B]">
                        {formatZAR(order.priceCents)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </section>
    </>
  );
}
